import httpClient from '../../http/client';
import { backendURL } from '../../lib/config';
import { StoredAddress } from '../address/addressSlice';
import { SignUP } from '../signup/signupSlice';
import { Plan } from '../../models/plans';

/**
 * The Community Fibre checkout. A DGtek sign up books an installer and goes
 * to /install; Community Fibre install it themselves, so there is no booking
 * and the order goes to /orders, which writes the Order record, sets billing
 * up and places the order with them.
 */

export interface OrderContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/** The body POST /api/v1/pineapple/orders takes. */
export interface OrderRequest {
  gnaf_id: string;
  /** The building the unit is in, which is how the CRM keys it. */
  building_gnaf_id?: string;
  /** The Community Fibre surface to order on: mef or npis. Left out, the backend's default. */
  order_api?: 'mef' | 'npis';
  plan_id: string;
  preferred_date: string;
  add_ons?: string[];
  contact: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  address: {
    unit?: string;
    street_number: string;
    street: string;
    suburb: string;
    state: string;
    postcode: string;
  };
  charge_bee?: Record<string, unknown>;
}

export interface OrderResponse {
  order_id: string;
  service_id: string;
  status: string;
  expected_date?: string;
  community_fibre_order_id?: string;
  cb_subscription_id?: string;
  hubspot_deal_id?: string;
  hubspot_building_id?: string;
}

/**
 * A Community Fibre plan is one the catalogue puts on that network. The id
 * is a fallback: master's are cf-<download>-<upload>, production's CF-100,
 * CF-500, CF-1000 and CF-2000.
 */
export const isCommunityFibrePlan = (plan?: Pick<Plan, 'id' | 'network'>): boolean => (
  plan?.network === 'communityfibre' || /^cf-/i.test(plan?.id ?? '')
);

export interface BuildOrderArgs {
  signUp: SignUP;
  address: StoredAddress;
  contact: OrderContact;
  /** The day the customer picked, YYYY-MM-DD. */
  preferred: string;
  /** From the qualification, which reads it out of G-NAF. */
  buildingGnafId?: string;
  /** The flags' say on the Community Fibre surface; left out, the backend decides. */
  orderApi?: 'mef' | 'npis';
}

export const buildOrderRequest = ({
  signUp,
  address,
  contact,
  preferred,
  buildingGnafId,
  orderApi,
}: BuildOrderArgs): OrderRequest => {
  const body: OrderRequest = {
    gnaf_id: address.gnafId ?? '',
    plan_id: signUp.plan.id,
    preferred_date: preferred,
    contact: {
      first_name: contact.firstName,
      last_name: contact.lastName,
      email: contact.email,
      phone: contact.phone,
    },
    address: {
      ...(address.unit ? { unit: address.unit } : {}),
      street_number: address.street_number,
      street: address.street,
      suburb: address.suburb,
      state: address.state,
      postcode: address.postcode,
    },
  };
  if (buildingGnafId) {
    body.building_gnaf_id = buildingGnafId;
  }
  if (orderApi) {
    body.order_api = orderApi;
  }
  if (signUp.addOns?.length) {
    body.add_ons = signUp.addOns;
  }
  // No card, no subscription: the backend places the order on its own, which
  // is how it is exercised before the Community Fibre plans exist in
  // Chargebee.
  if (signUp.cb_token) {
    body.charge_bee = {
      plan_id: signUp.plan.id,
      tmp_token: signUp.cb_token,
      first_name: contact.firstName,
      last_name: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      unit: address.unit ?? '',
      address: `${address.street_number} ${address.street}`,
      suburb: address.suburb,
      state: address.state,
      postcode: address.postcode,
      add_ons: signUp.addOns,
      coupon_id: signUp.coupon?.coupon_id,
      coupon_type: signUp.coupon?.coupon_type,
      utm_source: signUp.utm?.source,
      utm_medium: signUp.utm?.medium,
      utm_campaign: signUp.utm?.campaign,
    };
  }
  return body;
};

export const createOrder = (body: OrderRequest) => httpClient.post<OrderResponse>(
  `${backendURL}/api/v1/pineapple/orders`,
  body,
);
